/*
 * PLUPack: movement for the Liquid pages (Nosotros, Contacto, tienda).
 *
 * The same vocabulary as the homepage:
 *   [data-plu-reveal="left|right|up"]  slides in from a side once it reaches the screen
 *   [data-plu-stagger="left|right|up"] does the same for each child, one after another
 *   [data-plu-converge] + [data-plu-line="left|right"]
 *                                      statement lines that meet from opposite sides with the scroll
 *   [data-plu-parallax="12"]           photo that drifts inside its frame with the scroll
 *   [data-plu-timeline]                steps that light up while a line fills with the scroll
 *   [data-plu-count]                   figure that counts up once ("+2.000", "300")
 *   [data-plu-slider]                  row that can be dragged, swiped or moved with arrows
 *   [data-plu-pointer]                 hero that follows the pointer (sets --plu-px / --plu-py)
 *
 * The page stays complete without this file: reveals only hide content once
 * the inline snippet has added .plu-motion to <html>, and that class is
 * removed if this file fails to load. With reduced motion (.plu-calm)
 * nothing slides or drifts: things fade in, figures still count and the
 * timeline still fills.
 */
(() => {
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  /* ---------- Reveal ---------- */

  /** Shows an element, or every child of a staggered group. */
  function reveal(element) {
    if (element.hasAttribute('data-plu-stagger')) {
      Array.from(element.children).forEach((child) => child.classList.add('is-in'));
    } else {
      element.classList.add('is-in');
    }
  }

  // Elements still waiting to appear. The observer reveals them as they
  // enter; the scroll check below also catches any the visitor scrolled
  // past too fast for the observer to see them on screen.
  const pendingReveals = new Set();

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              reveal(entry.target);
              revealObserver.unobserve(entry.target);
              pendingReveals.delete(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
        )
      : null;

  function revealPassed() {
    if (!pendingReveals.size) return;
    const limit = (window.innerHeight || 0) * 0.9;
    pendingReveals.forEach((element) => {
      if (!element.isConnected) {
        pendingReveals.delete(element);
        return;
      }
      if (element.getBoundingClientRect().top < limit) {
        reveal(element);
        revealObserver?.unobserve(element);
        pendingReveals.delete(element);
      }
    });
  }

  function setupReveal(scope) {
    // A staggered group is watched as a whole, so items of a sideways row
    // that start off screen come in together with the visible ones.
    const groups = Array.from(scope.querySelectorAll('[data-plu-stagger]'));
    const grouped = new Set();
    groups.forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        if (!child.hasAttribute('data-plu-reveal')) {
          child.setAttribute('data-plu-reveal', group.dataset.pluStagger || 'up');
        }
        child.style.setProperty('--plu-i', String(Math.min(index, 8)));
        grouped.add(child);
      });
    });

    const targets = [
      ...groups,
      ...Array.from(scope.querySelectorAll('[data-plu-reveal]')).filter((element) => !grouped.has(element)),
    ];
    targets.forEach((element) => {
      if (!revealObserver) {
        reveal(element);
      } else {
        revealObserver.observe(element);
        pendingReveals.add(element);
      }
    });
  }

  /* ---------- Scroll-linked effects ---------- */

  const scrubbers = [];
  let frame = 0;

  /** 0 when the element's top reaches `from` (fraction of the viewport), 1 at `to`. */
  function progressBetween(rect, from, to) {
    const viewport = window.innerHeight || 1;
    return clamp((viewport * from - rect.top) / (viewport * (from - to)));
  }

  function setupScrub(scope) {
    scope.querySelectorAll('[data-plu-converge]').forEach((element) => {
      const lines = element.querySelectorAll('[data-plu-line]');
      if (!lines.length) return;
      const distance = parseFloat(element.dataset.pluConverge) || 16;
      scrubbers.push({
        element,
        update(rect) {
          const p = progressBetween(rect, 1, 0.42);
          const eased = 1 - Math.pow(1 - p, 2);
          lines.forEach((line) => {
            const side = line.dataset.pluLine === 'right' ? 1 : -1;
            if (!reduced) {
              line.style.transform = `translate3d(${(side * (1 - eased) * distance).toFixed(2)}vw, 0, 0)`;
            }
            line.style.opacity = (0.15 + 0.85 * eased).toFixed(3);
          });
        },
      });
    });

    scope.querySelectorAll('[data-plu-parallax]').forEach((element) => {
      if (reduced) return;
      const amount = parseFloat(element.dataset.pluParallax) || 10;
      const frameElement = element.parentElement || element;
      scrubbers.push({
        element: frameElement,
        update(rect) {
          const viewport = window.innerHeight || 1;
          const p = clamp((viewport - rect.top) / (viewport + rect.height));
          element.style.transform = `translate3d(0, ${((0.5 - p) * amount).toFixed(2)}%, 0) scale(1.14)`;
        },
      });
    });

    scope.querySelectorAll('[data-plu-timeline]').forEach((element) => {
      const steps = Array.from(element.querySelectorAll('[data-plu-step]'));
      scrubbers.push({
        element,
        update(rect) {
          const p = progressBetween(rect, 0.82, 0.3);
          element.style.setProperty('--plu-p', p.toFixed(3));
          const last = Math.max(1, steps.length - 1);
          steps.forEach((step, index) => {
            step.classList.toggle('is-lit', p >= index / last - 0.02);
          });
        },
      });
    });
  }

  function renderScrub() {
    frame = 0;
    revealPassed();
    for (let i = scrubbers.length - 1; i >= 0; i -= 1) {
      const scrubber = scrubbers[i];
      if (!scrubber.element.isConnected) {
        scrubbers.splice(i, 1);
        continue;
      }
      const rect = scrubber.element.getBoundingClientRect();
      const viewport = window.innerHeight;
      if (rect.bottom < -viewport * 0.5 || rect.top > viewport * 1.5) continue;
      scrubber.update(rect);
    }
  }

  function requestScrub() {
    if (!frame) frame = window.requestAnimationFrame(renderScrub);
  }

  window.addEventListener('scroll', requestScrub, { passive: true });
  window.addEventListener('resize', requestScrub);

  /* ---------- Count up ---------- */

  const countObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              countObserver.unobserve(entry.target);
              runCount(entry.target);
            });
          },
          { threshold: 0.6 },
        )
      : null;

  function parseFigure(text) {
    const match = text.match(/^(\D*?)(\d[\d.,]*)(.*)$/s);
    if (!match) return null;
    const digits = match[2].replace(/[.,](?=\d{3}(\D|$))/g, '');
    const value = Number(digits.replace(',', '.'));
    if (!Number.isFinite(value)) return null;
    return { prefix: match[1], value, suffix: match[3], grouped: /[.,]\d{3}/.test(match[2]) };
  }

  function formatFigure(figure, value) {
    const rounded = Math.round(value);
    const number = figure.grouped || rounded >= 10000 ? rounded.toLocaleString('es-AR') : String(rounded);
    return `${figure.prefix}${number}${figure.suffix}`;
  }

  function runCount(element) {
    const figure = element.pluFigure;
    if (!figure) return;
    const duration = 1600;
    const start = performance.now();
    const step = (now) => {
      const t = clamp((now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      element.textContent = formatFigure(figure, figure.value * eased);
      if (t < 1) window.requestAnimationFrame(step);
      else element.textContent = element.pluOriginal;
    };
    window.requestAnimationFrame(step);
  }

  function setupCount(scope) {
    if (!countObserver) return;
    scope.querySelectorAll('[data-plu-count]').forEach((element) => {
      if (element.pluFigure) return;
      const original = element.textContent.trim();
      const figure = parseFigure(original);
      if (!figure || figure.value === 0) return;
      element.pluOriginal = original;
      element.pluFigure = figure;
      element.setAttribute('aria-label', original);
      element.textContent = formatFigure(figure, 0);
      countObserver.observe(element);
    });
  }

  /* ---------- Sliders ---------- */

  function setupSlider(slider) {
    if (slider.pluSlider) return;
    slider.pluSlider = true;
    const track = slider.querySelector('[data-plu-track]');
    if (!track) return;
    const prev = slider.querySelector('[data-plu-prev]');
    const next = slider.querySelector('[data-plu-next]');
    const thumb = slider.querySelector('[data-plu-thumb]');

    const stride = () => {
      const item = track.firstElementChild;
      if (!item) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    };

    const measure = () => {
      const max = track.scrollWidth - track.clientWidth;
      const scrollable = max > 4;
      slider.classList.toggle('is-static', !scrollable);
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
      if (thumb) {
        const size = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        const p = max > 0 ? track.scrollLeft / max : 0;
        thumb.style.width = `${Math.max(size, 0.1) * 100}%`;
        thumb.style.transform = `translateX(${((p * (1 - size)) / Math.max(size, 0.1)) * 100}%)`;
      }
    };

    const page = (direction) => {
      const step = stride();
      const perView = Math.max(1, Math.floor(track.clientWidth / step));
      track.scrollBy({ left: direction * perView * step, behavior: reduced ? 'auto' : 'smooth' });
    };

    prev?.addEventListener('click', () => page(-1));
    next?.addEventListener('click', () => page(1));
    track.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(track);

    // Mouse drag; touch screens swipe natively.
    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, scroll: track.scrollLeft, moved: false, id: event.pointerId };
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved && Math.abs(delta) > 6) {
        drag.moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(drag.id);
      }
      if (drag.moved) track.scrollLeft = drag.scroll - delta;
    });
    const end = () => {
      if (!drag) return;
      const moved = drag.moved;
      if (moved && track.hasPointerCapture(drag.id)) track.releasePointerCapture(drag.id);
      drag = moved ? { moved: true } : null;
      if (!moved) return;
      // The click that follows this release must not open a link; anything
      // after it (a keyboard press, say) is a normal click again.
      window.setTimeout(() => {
        drag = null;
      }, 0);
      const step = stride();
      track.scrollTo({ left: Math.round(track.scrollLeft / step) * step, behavior: 'smooth' });
      window.setTimeout(() => track.classList.remove('is-dragging'), 420);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener(
      'click',
      (event) => {
        if (drag && drag.moved) {
          event.preventDefault();
          event.stopPropagation();
        }
        drag = null;
      },
      true,
    );
    track.addEventListener('dragstart', (event) => event.preventDefault());

    measure();
  }

  /* ---------- Pointer ---------- */

  function setupPointer(element) {
    if (reduced || element.pluPointer || !window.matchMedia('(hover: hover)').matches) return;
    element.pluPointer = true;
    let raf = 0;
    let x = 0;
    let y = 0;
    element.addEventListener('pointermove', (event) => {
      const rect = element.getBoundingClientRect();
      x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        element.style.setProperty('--plu-px', x.toFixed(3));
        element.style.setProperty('--plu-py', y.toFixed(3));
      });
    });
    element.addEventListener('pointerleave', () => {
      element.style.setProperty('--plu-px', '0');
      element.style.setProperty('--plu-py', '0');
    });
  }

  /* ---------- Start ---------- */

  function setup(scope = document) {
    setupReveal(scope);
    setupScrub(scope);
    setupCount(scope);
    scope.querySelectorAll('[data-plu-slider]').forEach(setupSlider);
    scope.querySelectorAll('[data-plu-pointer]').forEach(setupPointer);
    requestScrub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup());
  } else {
    setup();
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
